import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Breadcrumb from 'component/Breadcrumb';
import styles from './index.module.scss';
import { RootState } from 'model';
import {
  deleteMeetingMember,
  cancelMeetingV2,
  getMeetingSummary,
  getMeetingDetailsV2,
  revokeMeeting,
  changeAttendState,
  publishMeetingSummary,
  publishMeetingContent,
  addMeetingMember,
  closeMeetingV2,
  deleteMeeting,
  cancelRepeatMeeting,
  cancelThisRepeatMeeting
} from 'model/meeting';
import { AttendState, OrderMembers } from 'type/meeting';
import { getPageFrom } from 'util/meeting';
import { usePageView, useTimeOnPage } from '@cvte/react-fridayagent3';
import FRIDAYAGENT_EVENTS from 'constant/fridayAgentEvent';
import Wrapper from './wrapper';
import { signRoom } from 'model/room';
import { getStaffId, getUserToken } from 'data/storage';
import AddMeetingMember from 'component/AddMeetingMember/meetingMember';
import { Relation } from 'type/contact';
import { addContact } from 'model/contact';
import APP_ROUTE from 'constant/route';
import container from 'lib/container';
import { MeetingOptionType } from 'type/meetingDetail';
import UploadSDK from 'lib/upload';
import tracker from 'lib/tracker';
import { TaskOptions } from '@isp/upload-sdk/dist/types/type';
import { associationFolder } from 'api/meeting';
import { isEqual } from 'lodash';

const Details: React.FC = () => {
  const { id } = useParams();
  const meeting = useSelector((state: RootState) => state.meeting.meetingDetailV2);
  const meetingSummary = useSelector((state: RootState) => state.meeting.meetingSummary);
  const beEmbedded = useSelector((state: RootState) => state.app.beEmbedded);
  const orderSettings = useSelector((state: RootState) => state.meeting.orderSettings);

  const dispatch = useDispatch();
  const history = useHistory();
  const [crumbItems, setCrumbItems] = useState<{ path?: string; label: string }[]>([]);
  const [showAddMeetingMember, setShowAddMeetingMember] = useState(false);
  const [wrapCount, setWrapCount] = useState(0);
  const [platformPlaceholder, setPlatformPlaceholder] = useState('正在获取...');
  const [meetingState, setMeetingState] = useState(meeting);

  let timer: NodeJS.Timer | undefined;

  // 埋点
  usePageView({ ...FRIDAYAGENT_EVENTS.WEB_TEAMS_PAGEVIEW_MEETING_DETAIL, delay: 1000 });
  useTimeOnPage({ ...FRIDAYAGENT_EVENTS.WEB_TEAMS_TIMEONPAGE_MEETING_DETAIL, min: 1000 });

  useEffect(() => {
    if (!id) return;
    dispatch(getMeetingDetailsV2(id));
    dispatch(getMeetingSummary(id));
    // 区分会议预约和会议安排
    setCrumbItems([getPageFrom(), { label: '会议详情' }]);
  }, [id]);

  useEffect(() => {
    if (!isEqual(meetingState, meeting)) {
      setMeetingState(meeting);
    }
    if (!meeting) return;
    if (
      (!(orderSettings as any)?.meetingOnline || (orderSettings as any)?.meetingOnline.show) &&
      !meeting.thirdPartyMeetingViewList &&
      meeting.meetingAllocationViews &&
      meeting.meetingAllocationViews.meetingTypes &&
      meeting.meetingAllocationViews.meetingTypes.length > 0
    ) {
      if (wrapCount < 60) {
        setPlatformPlaceholder('正在获取...');
        timer = setTimeout(() => {
          dispatch(getMeetingDetailsV2(id));
          setWrapCount(wrapCount + 1);
        }, 1000);
      } else {
        setPlatformPlaceholder('获取失败');
      }
    }
    return () => {
      timer && clearTimeout(timer);
    };
  }, [meeting]);

  const addMeetingMemberSubmit = (members: any[]) => {
    const orderMembers: OrderMembers = {
      ids: members.map((m) => m.refId),
      fromRecommended: []
    };
    dispatch(addMeetingMember({ meetingId: id, members: orderMembers }));
    members
      .filter((m) => m.relation === Relation.NONE)
      .forEach((nm) => {
        dispatch(addContact({ id: nm._id || '', staffId: nm.staffId }));
      });
  };

  const refresh = () => {
    setTimeout(() => {
      dispatch(getMeetingDetailsV2(id));
      dispatch(getMeetingSummary(id));
    }, 1500);
  };

  const handelClickEdit = (type: MeetingOptionType) => {
    // 解析现有的查询字符串
    const searchParams = new URLSearchParams(location.search);

    // 添加新的参数，如果存在
    if (type) {
      searchParams.set('editType', type);
    }

    // 构建新的查询字符串
    const newSearch = searchParams.toString();

    container.sdk?.editMeeting(id, newSearch);
    tracker?.reporteSchOperation('修改会议');
    history.push(`/edit/${id}?${newSearch}`);
  };

  const handelClickCancel = (type: MeetingOptionType) => {
    if (!meetingState) return;
    // TODO 取消会议【待联调】
    tracker?.reporteSchOperation('取消会议');
    let time = 1000;
    switch (type) {
      case MeetingOptionType.ThisMeeting:
        dispatch(cancelThisRepeatMeeting(id));
        break;
      case MeetingOptionType.AllMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meetingState.repeatMeetingNo }));
        break;
      case MeetingOptionType.PartMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meetingState.repeatMeetingNo, startTimeGe: meetingState.beginTime }));
        break;
      case MeetingOptionType.SingleMeeting:
        dispatch(cancelMeetingV2(id));
        break;
    }
    setTimeout(() => {
      container.sdk?.cancelMeetingSuccess(id);
      history.push(APP_ROUTE.SCHEDULE);
    }, time);
  };

  const handelClickInvite = () => {
    tracker?.reporteSchOperation('邀请参会人');
    setShowAddMeetingMember(true);
  };

  const handelClickSignIn = () => {
    dispatch(signRoom(id));
  };

  // 结束会议
  const handelClickConclude = () => {
    tracker?.reporteSchOperation('结束会议');
    dispatch(closeMeetingV2(id));
  };

  // 撤销审批会议
  const handelClickRevoke = () => {
    tracker?.reporteSchOperation('撤销审批会议');
    dispatch(revokeMeeting(id));
  };

  const handelClickAttendState = (state: AttendState) => {
    dispatch(
      changeAttendState({
        meetingId: id,
        state: state
      })
    );
    if (state === AttendState.REFUSED) {
      history.goBack();
    } else {
      refresh();
    }
  };

  const handelClickSubmitNotes = (event: any) => {
    const text = event.detail.join('\n');
    tracker?.reporteSchOperation('提交纪要');
    dispatch(
      publishMeetingSummary({
        meetingId: id,
        subject: meetingState?.subject ?? '',
        content: text,
        staffId: getStaffId()
      })
    );
  };

  const handelClickAgendaEdit = (event: any) => {
    const text = event.detail.join('\n');
    tracker?.reporteSchOperation('议程');
    dispatch(
      publishMeetingContent({
        meetingId: id,
        content: text
      })
    );
  };

  const handelClickWithdrawInvite = (event: any) => {
    dispatch(deleteMeetingMember({ meetingId: id, members: { ids: event.detail ?? [] } }));
  };

  const handleClickGotIt = () => {
    dispatch(deleteMeeting(id));

    setTimeout(() => {
      container.sdk?.cancelMeetingSuccess(id);
      history.push(APP_ROUTE.SCHEDULE);
    }, 1000);
  };

  const handleClickLink = (e: any) => {
    let url = e?.detail?.[0] || '';
    const userToken = getUserToken();
    if (!url || !userToken || !id) return;
    url = url.replace('{userToken}', userToken).replace('{meetingNo}', id);
    window.open(url);
  };

  const onUploadFile = (fileOption: TaskOptions) => {
    console.log('----fileOption', fileOption);

    return UploadSDK.addTask({
      ...fileOption,
      onSuccess(res) {
        fileOption.onSuccess?.(res);
        if (meetingState) {
          associationFolder(meetingState.meetingNo, {
            fileKey: res.fileKey,
            fileName: fileOption.name,
            contentType: fileOption.file?.type || '',
            size: res.fileSize
          });
        }
      },
      onError(err) {
        fileOption.onError?.(err);
        console.error('上传文件出现异常 onError', err);
      }
    });
  };

  return (
    <>
      <Breadcrumb items={crumbItems}></Breadcrumb>
      {meetingState && meetingState.meetingNo === id && (
        <>
          <div className={styles.meetingInfo} style={{ padding: beEmbedded ? '0 24px' : '0 100px' }}>
            <Wrapper
              meetingInfo={meetingState}
              linkList={orderSettings?.linkList}
              detailSettings={orderSettings}
              meetingSummary={meetingSummary ? meetingSummary.content : ''}
              platformPlaceholder={platformPlaceholder}
              onClickEdit={() => handelClickEdit(MeetingOptionType.SingleMeeting)}
              onClickCancel={() => handelClickCancel(MeetingOptionType.SingleMeeting)}
              onClickSignIn={handelClickSignIn}
              onClickConclude={handelClickConclude}
              onClickRevoke={handelClickRevoke}
              onClickJoin={() => handelClickAttendState(AttendState.JOINED)}
              onClickAskOff={() => handelClickAttendState(AttendState.ASK_FOR_LEAVE)}
              onClickRefuse={() => handelClickAttendState(AttendState.REFUSED)}
              onClickSubmitNotes={handelClickSubmitNotes}
              onClickWithdrawInvite={(userId: string) => {
                handelClickWithdrawInvite(userId);
              }}
              onClickInvite={handelClickInvite}
              onClickGotIt={handleClickGotIt}
              onClickAgendaEdit={handelClickAgendaEdit}
              onClickEditThis={() => handelClickEdit(MeetingOptionType.ThisMeeting)}
              onClickEditAll={() => handelClickEdit(MeetingOptionType.AllMeeting)}
              onClickCancelAll={() => handelClickCancel(MeetingOptionType.AllMeeting)}
              onClickCancelThis={() => handelClickCancel(MeetingOptionType.ThisMeeting)}
              onClickCancelPart={() => handelClickCancel(MeetingOptionType.PartMeeting)}
              onClickLink={handleClickLink}
              uploadFile={onUploadFile}
            />
          </div>
          <AddMeetingMember
            beEmbedded={beEmbedded}
            show={showAddMeetingMember}
            close={() => setShowAddMeetingMember(false)}
            disabledList={meetingState.members.map((m) => m.user)}
            onSubmit={(selectPerson) => addMeetingMemberSubmit(selectPerson)}
          />
        </>
      )}
    </>
  );
};

export default Details;
